export interface HaulbackReasonCode {
  id: string;
  reasonCode: string;
  /** Language the `reasonCode` label reflects (set by the list's language filter). */
  language: string;
  displayOrder: number;
  reasonCodeCategory: 'Delivery' | 'Equipment' | 'Sales' | 'Warehouse';
  locationTypes: string[];
  orgTypes: string[];
  tplStatuses: string[];
  duoStatuses: string[];
  pictureRequired: boolean;
  pictureOptional: boolean;
  pictureTitle: string;
  textFieldRequired: boolean;
  textFieldOptional: boolean;
  textFieldTitle: string;
  supervisorNotificationWarning: boolean;
  supervisorAutomaticNotification: boolean;
  active: boolean;
  icon: string;
}

export interface RouteRoadblock {
  id: string;
  roadblockType: 'Stop Issue' | 'Route Issue';
  routeRoadblock: string;
  /** Language the `routeRoadblock` label reflects (set by the list's language filter). */
  language: string;
  displayOrder: number | null;
  reasonCodeCategory: 'Delivery' | 'Equipment' | 'Sales' | 'Warehouse';
  locationTypes: string[];
  orgTypes: string[];
  tplStatuses: string[];
  duoStatuses: string[];
  pictureRequired: boolean;
  pictureOptional: boolean;
  pictureTitle: string;
  textFieldRequired: boolean;
  textFieldOptional: boolean;
  textFieldTitle: string;
  supervisorNotificationWarning: boolean;
  supervisorAutomaticNotification: boolean;
  active: boolean;
  icon: string;
}

// Note: LOCATION_TYPES and ORG_TYPES are loaded from Redux masterdata.
// See HaulbacksTab.tsx and RouteRoadblocksTab.tsx for usage via useSelector.
export const TPL_STATUSES = ['3PL', 'Non-3PL'];
export const DUO_STATUSES = ['DUO', 'Non-DUO'];
export const ROADBLOCK_TYPES = ['Stop Issue', 'Route Issue'] as const;
export const REASON_CODE_CATEGORIES = ['Delivery', 'Equipment', 'Sales', 'Warehouse'] as const;

// ─── API adapters ─────────────────────────────────────────────────────────────
// Converts backend DTOs (which use `isActive` / `threePlStatuses`) to the local
// UI shapes used across Admin tabs (which use `active` / `tplStatuses`).

import type { HaulbackResponseDTO, RouteRoadblockResponseDTO } from '../../../../api/deliveryRpmApi';

export function haulbackFromDTO(dto: HaulbackResponseDTO): HaulbackReasonCode {
  return {
    id: String(dto.id),
    reasonCode: dto.reasonCode,
    language: dto.language ?? 'en',
    displayOrder: dto.displayOrder,
    reasonCodeCategory: (dto.reasonCodeCategory || 'Delivery') as 'Delivery' | 'Equipment' | 'Sales' | 'Warehouse',
    locationTypes: dto.locationTypes,
    orgTypes: dto.orgTypes,
    tplStatuses: dto.threePlStatuses,
    duoStatuses: dto.duoStatuses,
    pictureRequired: dto.pictureRequired,
    pictureOptional: dto.pictureOptional ?? false,
    pictureTitle: dto.pictureTitle ?? '',
    textFieldRequired: dto.textFieldRequired,
    textFieldOptional: dto.textFieldOptional ?? false,
    textFieldTitle: dto.textFieldTitle ?? '',
    supervisorNotificationWarning: dto.supervisorNotificationWarning ?? false,
    supervisorAutomaticNotification: dto.supervisorAutomaticNotification ?? false,
    active: dto.isActive,
    icon: dto.iconName ?? '',
  };
}

export function roadblockFromDTO(dto: RouteRoadblockResponseDTO): RouteRoadblock {
  return {
    id: String(dto.id),
    roadblockType: dto.roadblockType,
    routeRoadblock: dto.routeRoadblock,
    language: dto.language ?? 'en',
    displayOrder: dto.displayOrder ?? null,
    reasonCodeCategory: (dto.reasonCodeCategory || 'Delivery') as 'Delivery' | 'Equipment' | 'Sales' | 'Warehouse',
    locationTypes: dto.locationTypes,
    orgTypes: dto.orgTypes,
    tplStatuses: dto.threePlStatuses,
    duoStatuses: dto.duoStatuses,
    pictureRequired: dto.pictureRequired ?? false,
    pictureOptional: dto.pictureOptional ?? false,
    pictureTitle: dto.pictureTitle ?? '',
    textFieldRequired: dto.textFieldRequired ?? false,
    textFieldOptional: dto.textFieldOptional ?? false,
    textFieldTitle: dto.textFieldTitle ?? '',
    supervisorNotificationWarning: dto.supervisorNotificationWarning ?? false,
    supervisorAutomaticNotification: dto.supervisorAutomaticNotification ?? false,
    active: dto.isActive,
    icon: dto.iconName ?? '',
  };
}
